import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { DomainConfigService } from '@domain/domain-config.service';
import { DomainRedirectService } from '@domain/domain-redirect.service';
import { LoggingService } from '@logging/logging.service';

/**
 * Route guard that enforces subdomain placement based on route metadata.
 *
 * Routes are classified into three categories via their `data` property:
 *
 * - **Root-domain only** (`data: { isRootDomainRoute: true }`):
 *   Must run on the root domain. Users on any other host are redirected to `<rootDomain>`.
 *   Examples: auth, logout, user-invitations.
 *
 * - **Shared** (`data: { allowAnyDomain: true }`):
 *   Accessible on both the root domain and the payroll subdomain — no redirect is performed.
 *   Examples: forbidden, locked, no-permissions, my-account, 404 catch-all.
 *
 * - **Payroll only** (default — no flag):
 *   Must run on the payroll subdomain. Users on any other host are redirected
 *   to `payroll.<rootDomain>`.
 *   Examples: payrolls, employers, employees, bureau, admin-hub, employee-hub.
 *
 * This "payroll by default" approach minimises configuration because most feature routes
 * belong on the payroll subdomain.
 *
 * When multi-domain is not enabled (`rootDomain` is not configured), this guard is a no-op.
 *
 * This guard is designed to be used as a `canActivateChild` guard on the root route
 * so it runs on every navigation.
 */
@Injectable({ providedIn: 'root' })
export class DomainRouteGuard {
  constructor(
    private domainConfig: DomainConfigService,
    private domainRedirect: DomainRedirectService,
    private logger: LoggingService
  ) {}

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    this.logger.trace(
      'DOMAIN GUARD: canActivateChild called',
      'state.url:',
      state.url,
      'hostname:',
      window.location.hostname,
      'href:',
      window.location.href,
      'childRoute.data:',
      JSON.stringify(childRoute.data || {})
    );
    return this.checkDomain(childRoute, state);
  }

  /**
   * Check whether the current host matches the expected subdomain for the given route.
   * If not, perform a full-page redirect to the correct host, preserving the full URL.
   *
   * The route tree is walked to find the first domain-related flag:
   * - `allowAnyDomain` → allow on any host (no redirect)
   * - `isRootDomainRoute` → must be on the login host
   * - (no flag) → must be on the payroll host
   *
   * @returns `true` if already on the correct host; `false` if a redirect was initiated
   */
  private checkDomain(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    if (this.domainRedirect.isRedirectInProgress) {
      this.logger.trace('DOMAIN GUARD: cross-domain redirect already in progress, blocking navigation');
      return false;
    }

    if (!this.domainConfig.isMultiDomainEnabled) {
      return true;
    }

    const domainScope = this.resolveDomainScope(childRoute);

    if (domainScope === 'any') {
      return true;
    }

    if (domainScope === 'root' && !this.domainConfig.isOnRootHost) {
      this.logger.trace(`DOMAIN GUARD: root-domain route "${state.url}" detected on non-root host — redirecting to root domain`);
      this.domainRedirect.redirectToRoot(state.url);
      return false;
    }

    if (domainScope === 'payroll' && !this.domainConfig.isOnPayrollHost) {
      this.logger.trace(`DOMAIN GUARD: payroll route "${state.url}" detected on non-payroll host — redirecting to payroll subdomain`);
      this.domainRedirect.redirectToPayroll(state.url);
      return false;
    }

    return true;
  }

  /**
   * Walk the activated route snapshot tree to determine the domain scope.
   *
   * Returns:
   * - `'any'` if any snapshot has `data.allowAnyDomain === true`
   * - `'root'` if any snapshot has `data.isRootDomainRoute === true`
   * - `'payroll'` if no domain flags are found (default)
   *
   * `allowAnyDomain` takes precedence over `isRootDomainRoute` to ensure that
   * shared pages (e.g. error pages) are never unexpectedly redirected.
   */
  private resolveDomainScope(route: ActivatedRouteSnapshot): 'root' | 'payroll' | 'any' {
    let isRoot = false;
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      if (current.data?.['allowAnyDomain'] === true) {
        return 'any';
      }
      if (current.data?.['isRootDomainRoute'] === true) {
        isRoot = true;
      }
      current = current.parent;
    }

    return isRoot ? 'root' : 'payroll';
  }
}
